import React, { useEffect, useMemo } from 'react';
import { styled } from '@compiled/react';
import { getIsMultiHCEnabled } from '@helpCenter/util/env';
import { getRelativeBasePath } from '@helpCenter/util/history';
import { LoadingSkeleton } from '@helpCenter/view/common/loading';
import { useInView } from 'react-intersection-observer';
import type { PreloadedQuery } from 'react-relay';
import { graphql, usePaginationFragment, usePreloadedQuery } from 'react-relay';
import * as grid from 'view/styles/grid';
import { AnalyticsContext } from '@atlaskit/analytics-next';
import { DropdownItemGroup } from '@atlaskit/dropdown-menu';
import { Box, xcss } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { ExperienceSuccess } from '@atlassian/ufo';
import { NUMBER_OF_HELP_CENTERS_PER_FETCH } from '../multi-hc-dropdown/constants';
import { multiHcDropdownExperience } from '../multi-hc-dropdown/experiences';
import { NoUnderlineDropdownItemWithAnalytics } from '../no-underline-dropdown-item-with-analytics';
import type { multiHcDropdownContentComponentQuery } from './__generated__/multiHcDropdownContentComponentQuery.graphql';
import type { multiHcDropdownContentFragment$key } from './__generated__/multiHcDropdownContentFragment.graphql';
import type {
    multiHcDropdownContentQuery,
    multiHcDropdownContentQuery$data,
} from './__generated__/multiHcDropdownContentQuery.graphql';

interface ComponentProps {
    toggleSeparatorVisibility: (isVisible: boolean) => void;
    renderQueryError: () => JSX.Element;
    onHelpCenterClick?: () => void;
    queryRef: PreloadedQuery<multiHcDropdownContentComponentQuery>;
}
interface ContentProps extends ComponentProps {
    helpCentersKey: multiHcDropdownContentQuery$data;
}

export const query = graphql`
    query multiHcDropdownContentComponentQuery(
        $cloudId: ID!
        $workspaceAri: ID!
        $SORT_ORDER: HelpCenterSortOrder!
        $NUMBER_OF_HELP_CENTERS_PER_FETCH: Int!
    ) {
        ...multiHcDropdownContentFragment
            @arguments(
                cloudId: $cloudId
                workspaceAri: $workspaceAri
                SORT_ORDER: $SORT_ORDER
                NUMBER_OF_HELP_CENTERS_PER_FETCH: $NUMBER_OF_HELP_CENTERS_PER_FETCH
            )
    }
`;
export const MultiHcDropdownContentComponent: React.FC<ComponentProps> = (props) => {
    const data = usePreloadedQuery(query, props.queryRef);
    return <MultiHcDropdownContent {...props} helpCentersKey={data} />;
};

const MultiHcDropdownContent: React.FC<ContentProps> = ({
    toggleSeparatorVisibility,
    renderQueryError,
    onHelpCenterClick,
    helpCentersKey,
}) => {
    const { loadNext, data, hasNext, isLoadingNext } = usePaginationFragment<
        multiHcDropdownContentQuery,
        multiHcDropdownContentFragment$key
    >(
        graphql`
            fragment multiHcDropdownContentFragment on Query
            @argumentDefinitions(
                cloudId: { type: "ID!" }
                workspaceAri: { type: "ID!" }
                SORT_ORDER: { type: "HelpCenterSortOrder!" }
                NUMBER_OF_HELP_CENTERS_PER_FETCH: { type: "Int!" }
                cursor: { type: "String" }
            )
            @refetchable(queryName: "multiHcDropdownContentQuery") {
                helpCenter(cloudId: $cloudId) {
                    helpCenters(
                        workspaceAri: $workspaceAri
                        sortOrder: $SORT_ORDER
                        first: $NUMBER_OF_HELP_CENTERS_PER_FETCH
                        after: $cursor
                    ) @connection(key: "multiHcDropdownContentFragment_helpCenters") {
                        edges {
                            node {
                                ... on HelpCenter {
                                    name {
                                        default
                                    }
                                    slug @optIn(to: "HelpCenterSlugTest")
                                }
                            }
                        }
                        nodes {
                            __typename
                        }
                        pageInfo {
                            endCursor
                            hasNextPage
                        }
                    }
                }
            }
        `,
        helpCentersKey
    );

    const helpCenters = useMemo(
        () =>
            data.helpCenter?.helpCenters?.edges
                ?.map((hc) => {
                    if (hc?.node?.slug) {
                        return {
                            hcname: hc?.node?.name?.default || 'Help Center',
                            url: `/helpcenter/${hc?.node?.slug}`,
                        };
                    }
                    //default helpcenter as slug is null
                    return {
                        hcname: hc?.node?.name?.default || 'Help Center',
                        url: '/servicedesk/customer',
                    };
                })
                //not showing the already selected HC in dropdown
                .filter((hc) => hc?.url !== getRelativeBasePath()) || [],
        [data]
    );

    useEffect(() => {
        if (helpCenters.length === 0) {
            toggleSeparatorVisibility(false);
        } else {
            toggleSeparatorVisibility(true);
        }
    }, [helpCenters, toggleSeparatorVisibility]);

    const { ref, inView } = useInView({
        threshold: 0,
    });

    useEffect(() => {
        if (hasNext && inView && !isLoadingNext) {
            loadNext(NUMBER_OF_HELP_CENTERS_PER_FETCH);
        }
    }, [hasNext, inView, isLoadingNext, loadNext]);

    const isDefault = (url: string) => url === '/servicedesk/customer';

    const hasQueryError = data.helpCenter?.helpCenters?.nodes?.[0]?.__typename === 'QueryError';
    return (
        <>
            {hasQueryError ? (
                renderQueryError()
            ) : (
                <StyledDropdownItemGroup id="multi-hc-dropdown-helpcenters" isScrollable>
                    {helpCenters.map((hc, hcIndex) => {
                        const shouldShowPaginationSentinel =
                            hcIndex === helpCenters.length - 5 && !isLoadingNext && hasNext;

                        const isLinkDisabled = !getIsMultiHCEnabled() && !isDefault(hc.url);
                        if (isLinkDisabled) {
                            return (
                                <>
                                    <NoUnderlineDropdownItemWithAnalytics key={hc.url} onClick={onHelpCenterClick}>
                                        {hc.hcname}
                                    </NoUnderlineDropdownItemWithAnalytics>
                                    {shouldShowPaginationSentinel && (
                                        <div className="pagination-sentinel" ref={ref}></div>
                                    )}
                                </>
                            );
                        }
                        return (
                            <>
                                <AnalyticsContext key={hc.url} data={{ actionSubjectId: 'viewHelpCenter' }}>
                                    <NoUnderlineDropdownItemWithAnalytics href={hc.url}>
                                        <Box xcss={menuItemStyles}>{hc.hcname}</Box>
                                    </NoUnderlineDropdownItemWithAnalytics>
                                </AnalyticsContext>
                                {shouldShowPaginationSentinel && <div className="pagination-sentinel" ref={ref}></div>}
                            </>
                        );
                    })}
                    {isLoadingNext && (
                        <Box xcss={skeletonPaddingContainer}>
                            <LoadingSkeleton height={16} backgroundColor={token('color.skeleton.subtle', colors.N20)} />
                        </Box>
                    )}

                    <ExperienceSuccess experience={multiHcDropdownExperience} />
                </StyledDropdownItemGroup>
            )}
        </>
    );
};

const menuItemStyles = xcss({
    color: 'color.text',
    textDecoration: 'none',
    maxWidth: grid.multiple(25).px,
});

const StyledDropdownItemGroup = styled(DropdownItemGroup)({
    maxHeight: grid.multiple(25).px,
});
const skeletonPaddingContainer = xcss({
    paddingBottom: 'space.100',
    paddingLeft: 'space.200',
    paddingRight: 'space.200',
    paddingTop: 'space.100',
});
